import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Banknote, Building2, Smartphone, Wallet2 } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { v: "Cash", icon: Banknote, tint: "emerald" },
  { v: "Bank", icon: Building2, tint: "sky" },
  { v: "Easypaisa", icon: Wallet2, tint: "emerald" },
  { v: "JazzCash", icon: Smartphone, tint: "amber" },
];

export default function PaymentDialog({ open, onOpenChange, loanId, loans, suggested, onSaved }) {
  const [form, setForm] = useState({
    loan_id: loanId || "",
    amount: "",
    date: new Date().toISOString().slice(0, 10),
    mode: "Cash",
    note: "",
  });
  const [loanList, setLoanList] = useState(loans || []);

  useEffect(() => {
    if (open) {
      setForm({
        loan_id: loanId || "",
        amount: suggested ? String(suggested) : "",
        date: new Date().toISOString().slice(0, 10),
        mode: "Cash",
        note: "",
      });
      if (!loans && !loanId) {
        api.get("/loans", { params: { status: "active" } }).then((r) => setLoanList(r.data));
      }
    }
  }, [open, loanId, suggested]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", {
        loan_id: form.loan_id,
        amount: Number(form.amount),
        date: form.date,
        mode: form.mode,
        note: form.note,
      });
      toast.success("Payment recorded");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!loanId && (
            <div className="space-y-1.5">
              <Label>Loan</Label>
              <select
                value={form.loan_id}
                onChange={(e) => setForm({ ...form, loan_id: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 h-10 bg-slate-50 text-sm"
                required
                data-testid="payment-loan-select"
              >
                <option value="">Select loan</option>
                {loanList.map((l) => (
                  <option key={l.id} value={l.id}>{l.customer_name} · {l.product_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount (PKR)</Label>
              <Input type="number" min={1} required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Payment mode</Label>
            <div className="grid grid-cols-2 gap-2">
              {MODES.map((m) => (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => setForm({ ...form, mode: m.v })}
                  className={`flex items-center gap-2 border rounded-md px-3 py-2.5 text-sm font-medium transition ${
                    form.mode === m.v
                      ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  data-testid={`mode-${m.v.toLowerCase()}`}
                >
                  <m.icon className="w-4 h-4" /> {m.v}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Note</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="payment-note-input" />
          </div>

          <DialogFooter>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="payment-save-btn">Save payment</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
